import { tierName } from '@h2h/shared';

/** "GOLD" → "Gold". */
export const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/** First letter of a name for an avatar monogram. */
export const initial = (name: string | undefined | null): string =>
  (name?.trim()?.[0] ?? 'P').toUpperCase();

/** Ordinal suffix only: 6 → "th", 1 → "st". */
export function ordinalSuffix(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** Tier index → display label, e.g. 2 → "Gold tier". */
export const tierLabel = (index: number): string => `${cap(tierName(index))} tier`;

/** Compact player count: 1 → "1", 2400 → "2.4K", 2_400_000 → "2.4M". */
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
}

/** "+235" / "−12" for signed point totals. */
export function signedPoints(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return '0';
}

/** Two-letter initials from a display name ("Maria A." → "MA"). */
export function initials(name: string): string {
  const parts = name.replace(/\./g, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Short tier code for compact ladder labels (e.g. 2 → "GOLD"). */
export function tierShort(index: number): string {
  const codes = ['BRZ', 'SLV', 'GOLD', 'PLAT', 'DIA', 'ELITE'] as const;
  return codes[Math.max(0, Math.min(codes.length - 1, index))] ?? '???';
}

/** Compact integer with thousands separator: 3420 → "3,420". */
export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** Card multiplier for display — cleans float noise (11.371499… → "11.4"). */
export function formatMultiplier(n: number): string {
  const rounded = Math.round(n * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  const oneDec = Math.round(rounded * 10) / 10;
  return oneDec % 1 === 0 ? String(oneDec) : oneDec.toFixed(1);
}

/** Rank delta label for standings rows: +4 → "▲ 4", -2 → "▼ 2", 0 → "–". */
export function rankDeltaLabel(delta: number | null | undefined): string {
  if (delta == null || delta === 0) return '–';
  return delta > 0 ? `▲ ${delta}` : `▼ ${Math.abs(delta)}`;
}

/** A short, human countdown to a future epoch-ms time: "2d 14h", "5h 12m", "8m". */
export function countdownTo(target: number, now: number = Date.now()): string {
  const ms = target - now;
  if (ms <= 0) return 'now';
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const minutes = mins % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Per-fixture lock countdown — includes seconds under an hour (picks card rows). */
export function countdownDetailed(target: number, now: number = Date.now()): string {
  const ms = target - now;
  if (ms <= 0) return 'Locked';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  done: boolean;
}

/** Full days/hours/minutes/seconds breakdown to a future epoch-ms time (clamped at 0). */
export function countdownParts(target: number, now: number = Date.now()): CountdownParts {
  const ms = Math.max(0, target - now);
  const totalSeconds = Math.floor(ms / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    done: ms <= 0
  };
}
